import React, { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { FileEdit, Trash2, ArrowRight, Inbox } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DraftsList = ({ onResume, refreshKey = 0 }) => {
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/drafts`);
      setDrafts(res.data || []);
    } catch (e) {
      toast.error("Erro ao carregar rascunhos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [refreshKey]);

  const discard = async (d) => {
    if (!window.confirm(`Descartar rascunho de ${d.report_date}?\nEsta ação não pode ser desfeita.`)) return;
    try {
      await axios.delete(`${API}/drafts/${d.id}`);
      toast.success("Rascunho descartado");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao descartar");
    }
  };

  return (
    <Card data-testid="drafts-list">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <FileEdit className="w-5 h-5 text-amber-600" /> Meus Rascunhos
          {drafts.length > 0 && <Badge variant="secondary" className="ml-2">{drafts.length}</Badge>}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-center py-6 text-slate-400">Carregando…</div>
        ) : drafts.length === 0 ? (
          <div className="text-center py-10 text-slate-500" data-testid="drafts-empty">
            <Inbox className="w-12 h-12 mx-auto mb-2 text-slate-300" />
            <div>Você não tem rascunhos pendentes.</div>
            <div className="text-xs text-slate-400 mt-1">RDOs salvos em rascunho aparecerão aqui até serem submetidos.</div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-xs text-slate-600">
                  <th className="py-2">Data do Relatório</th>
                  <th className="py-2">Versão</th>
                  <th className="py-2">Atualizado em</th>
                  <th className="py-2 text-center w-[200px]">Ações</th>
                </tr>
              </thead>
              <tbody>
                {drafts.map(d => (
                  <tr key={d.id} className="border-b hover:bg-amber-50/40" data-testid={`draft-row-${d.id}`}>
                    <td className="py-2 font-medium">{d.report_date}</td>
                    <td className="py-2 text-xs">v{d.version || 1}</td>
                    <td className="py-2 text-xs text-slate-500">
                      {d.updated_at ? new Date(d.updated_at).toLocaleString("pt-BR") : "—"}
                    </td>
                    <td className="py-2 text-center">
                      <div className="flex justify-center gap-2">
                        <Button
                          size="sm"
                          onClick={() => onResume?.(d)}
                          data-testid={`resume-draft-${d.id}`}
                          className="bg-amber-600 hover:bg-amber-700"
                        >
                          <ArrowRight className="w-4 h-4 mr-1" /> Continuar
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => discard(d)}
                          data-testid={`discard-draft-${d.id}`}
                          className="border-red-300 text-red-600 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default DraftsList;
